import { Button } from '@/components/ui/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/shadcn/dialog';
import { Input } from '@/components/ui/shadcn/input';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { Check, Edit2, Loader2, Mail, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface InlineEmailCellProps {
    value: string | null | undefined;
    onUpdate?: (value: string | null) => Promise<void>;
    className?: string;
    readOnly?: boolean;
}

export function InlineEmailCell({ value, onUpdate, className, readOnly = false }: InlineEmailCellProps) {
    const { t } = useTranslations();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editValue, setEditValue] = useState(value || '');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!isDialogOpen) {
            setEditValue(value || '');
        }
    }, [value, isDialogOpen]);

    const handleSave = async () => {
        const trimmedValue = editValue.trim();
        const normalizedValue = trimmedValue || null;

        if (normalizedValue === (value || null) || !onUpdate) {
            setIsDialogOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            await onUpdate(normalizedValue);
            setIsDialogOpen(false);
        } catch (error) {
            console.error('Failed to update email:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setEditValue(value || '');
        setIsDialogOpen(false);
    };

    // Read-only mode: just display the email
    if (readOnly || !onUpdate) {
        return (
            <div className={cn('flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm', className)}>
                <Mail className="text-muted-foreground h-4 w-4 shrink-0" />
                {value ? (
                    <a href={`mailto:${value}`} className="hover:underline">
                        <span className="max-w-[200px] truncate">{value}</span>
                    </a>
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </div>
        );
    }

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsDialogOpen(true);
                    }}
                    className={cn(
                        'hover:bg-accent w-full rounded px-2 py-1.5 text-left transition-colors',
                        'group flex items-center gap-2 text-sm',
                        !value && 'text-muted-foreground',
                        className,
                    )}
                >
                    <Mail className="text-muted-foreground h-4 w-4 shrink-0" />
                    {value ? (
                        <a href={`mailto:${value}`} className="max-w-[200px] truncate hover:underline" onClick={(e) => e.stopPropagation()}>
                            {value}
                        </a>
                    ) : (
                        <span>—</span>
                    )}
                    <Edit2 className="text-muted-foreground h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
                <DialogHeader>
                    <DialogTitle>{t('people.index.columns.email', 'Email')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <label className="mb-2 block text-sm font-medium">{t('people.index.columns.email', 'Email')}</label>
                        <Input
                            type="email"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            placeholder={t('people.index.columns.email_placeholder', 'Enter email address')}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSave();
                                } else if (e.key === 'Escape') {
                                    handleCancel();
                                }
                            }}
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
                            <X className="mr-2 h-4 w-4" />
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button onClick={handleSave} disabled={isLoading || editValue.trim() === (value || '')}>
                            {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                            {t('common.save', 'Save')}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
